import React, { useMemo } from "react";
import { Box } from "@mui/material";
// @ts-ignore
import { StriimDataTable, StriimFeedbackBlock, StriimTypography } from "@striim/striim-ui";
import EllipsisText from "src/generic/ellipsis-text/ellipsis-text";
import WaitIcon from "app/images/Wait.svg";
import UndrawDreamer from "app/images/undraw-dreamer.svg";
import { styles } from "./query-preview.styles";
import { QueryItem } from "../add-queries-modal/add-queries-modal";
import { isPreviewOutdated } from "../add-queries-modal/add-queries-modal.utils";
import { AdapterIcon } from "src/generic/icon/component-icon/component-icon";
import componentService from "core/services/componentService/component-service";

export interface QueryPreviewResponse {
    success: boolean;
    errorMessage: string | null;
    columns: string[];
    types: string[];
    rows: (string | number | boolean | null)[][];
    rowCount: number;
}

export interface QueryPreviewState {
    isLoading: boolean;
    data: QueryPreviewResponse | null;
    error: string | null;
    previewedSql: string | null;
}

interface QueryPreviewProps {
    query: QueryItem;
    state: QueryPreviewState | undefined;
    adapterName: string;
    databaseProviderType?: string;
}

const HeaderCell: React.FC<{ col: string; type: string }> = React.memo(({ col, type }) => (
    <Box>
        <StriimTypography variant="body3" color="greyscale.600" sx={{ display: "block" }}>
            {col}
        </StriimTypography>
        <StriimTypography variant="caption3" color="greyscale.600" sx={{ display: "block" }}>
            ({type})
        </StriimTypography>
    </Box>
));

const CellRenderer: React.FC<{ value: string | number | boolean | null }> = React.memo(({ value }) => (
    <EllipsisText text={value != null ? String(value) : ""} />
));

const QueryPreview: React.FC<QueryPreviewProps> = ({ query, state, adapterName, databaseProviderType }) => {
    const isLoading = state?.isLoading ?? false;
    const previewData = state?.data ?? null;
    const error = state?.error ?? null;
    const isOutdated = isPreviewOutdated({ state, currentSql: query.sql });

    const tableColumns = useMemo(() => {
        if (!previewData) return [];
        return previewData.columns.map((col, index) => ({
            headerName: col,
            field: col,
            minWidth: 160,
            flex: 1,
            suppressMovable: true,
            sortable: true,
            headerComponent: HeaderCell,
            headerComponentParams: { col, type: previewData.types[index] },
            cellRenderer: CellRenderer
        }));
    }, [previewData]);

    const tableData = useMemo(() => {
        if (!previewData) return [];
        return previewData.rows.map((row, rowIndex) => {
            const rowObj: Record<string, string | number | boolean | null> = { id: rowIndex };
            previewData.columns.forEach((col, colIndex) => {
                rowObj[col] = row[colIndex];
            });
            return rowObj;
        });
    }, [previewData]);

    if (isLoading) {
        return (
            <Box sx={styles.container}>
                <WaitIcon style={styles.waitImage} />
                <StriimTypography variant="h4" color="primary.900">
                    Loading query preview...
                </StriimTypography>
                <StriimTypography variant="body4" color="greyscale.700">
                    Results are being fetched. This may take a moment.
                </StriimTypography>
            </Box>
        );
    }

    const outdatedBanner = isOutdated ? (
        <Box width={"100%"} mb={1}>
            <StriimFeedbackBlock
                variant="warning"
                content={
                    <Box display="flex" flexDirection="column">
                        <StriimTypography variant="h3" color="greyscale.900">
                            Preview Outdated
                        </StriimTypography>
                        <StriimTypography variant="body4" color="greyscale.900">
                            The query has been modified since this preview was generated. Run the preview again to see
                            updated results.
                        </StriimTypography>
                    </Box>
                }
            />
        </Box>
    ) : null;

    if (error) {
        return (
            <Box display={"flex"} flexDirection={"column"} alignItems={"center"} p={2} data-test-id="preview-error">
                {outdatedBanner}
                <StriimFeedbackBlock
                    variant="critical"
                    content={
                        <Box display={"flex"} flexDirection={"column"}>
                            <StriimTypography variant="h3" color={"greyscale.900"}>
                                Query preview failed
                            </StriimTypography>
                            <StriimTypography variant="body4" color="greyscale.900">
                                {error}
                            </StriimTypography>
                        </Box>
                    }
                />
                <UndrawDreamer style={styles.undrawImage} />
            </Box>
        );
    }

    if (!previewData) {
        return (
            <Box sx={styles.container}>
                <WaitIcon style={styles.waitImage} />
                <StriimTypography variant="h4" color="primary.900">
                    Loading query preview...
                </StriimTypography>
                <StriimTypography variant="body4" color="greyscale.700">
                    Results are being fetched. This may take a moment.
                </StriimTypography>
            </Box>
        );
    }

    const hasNoColumns = previewData.columns?.length === 0;

    return (
        <Box data-test-id="preview-content">
            {outdatedBanner}
            <Box sx={styles.previewHeader}>
                <AdapterIcon
                    size={40}
                    showBorder={false}
                    type={"source"}
                    useNative
                    name={componentService.getIconNameByAdapterNameDBType(adapterName, databaseProviderType)}
                />
                <StriimTypography variant="h4" color="primary.900">
                    {query.identifier} Preview
                </StriimTypography>
            </Box>
            {hasNoColumns ? (
                <Box sx={styles.container} data-test-id="preview-table">
                    <StriimTypography variant="h4" color="primary.900">
                        No data to display
                    </StriimTypography>
                </Box>
            ) : (
                <Box sx={styles.tableContainer} data-test-id="preview-table">
                    <StriimDataTable data={tableData} columns={tableColumns} maxHeight={300} />
                </Box>
            )}
        </Box>
    );
};

export default QueryPreview;
